import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Box,
  Grid,
  Stack,
  Button,
  Dialog,
  useTheme,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  useMediaQuery
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import {
  useCreateBookableEventTypeMutation,
  useUpdateBookableEventTypeByIdMutation,
} from 'src/services/calendar/calendar.service';
import {
  EventTypeStatus,
  BookableEventType,
  CreateBookableEventTypeDto,
  UpdateBookableEventTypeDto,
} from 'src/services/calendar/calendar.types';

import Iconify from 'src/components/iconify';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';
import FormProvider, {
  RHFSwitch,
  RHFTextField,
} from 'src/components/hook-form';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

import EventTypeAvailabilityModal from './event-type-availability-modal';

type BookableEventTypeModalProps = {
  open: boolean;
  onClose: () => void;
  eventType: BookableEventType | null;
};

export default function EventTypeEditorModal({
  open,
  onClose,
  eventType,
}: BookableEventTypeModalProps) {

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { canAccess } = useFeatures();
  const availabilityModalControl = useBoolean();

  const [submitting, setSubmitting] = useState(false);
  const [createEventType, { isLoading: isCreating }] = useCreateBookableEventTypeMutation();
  const [updateEventType, { isLoading: isUpdating }] = useUpdateBookableEventTypeByIdMutation();

  // Form validation schema
  const EventTypeSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    duration_minutes: Yup.number()
      .required(t('validation.required'))
      .min(5, t('validation.min', { min: 5 }))
      .max(480, t('validation.max', { max: 480 })), // Max 8 hours
    buffer_minutes: Yup.number()
      .min(0, t('validation.min', { min: 0 }))
      .max(60, t('validation.max', { max: 60 })),

    // cannot be more than duration_minutes
    slot_increment_minutes: Yup.number()
      .required(t('validation.required'))
      .min(10, t('validation.min', { min: 10 }))
      .max(60, t('validation.max', { max: 60 }))
      .test('is-less-than-duration', t('account.scheduling_settings.slot_time_duration_max_validation'), function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { duration_minutes } = this.parent;
        return value <= duration_minutes;
      }),
    location: Yup.string(),
    is_active: Yup.boolean(),
    autocreate_meeting: Yup.boolean(),
  });

  // Define the form value types
  type FormValues = {
    name: string;
    description: string;
    duration_minutes: number;
    slot_increment_minutes: number;
    buffer_minutes: number;
    location?: string;
    is_active: boolean;
    autocreate_meeting: boolean;
    create_meeting_bot: boolean;
  };

  const defaultValues: FormValues = {
    name: '',
    description: '',
    duration_minutes: 30,
    buffer_minutes: 0,
    slot_increment_minutes: 15,
    location: '',
    is_active: true,
    autocreate_meeting: false,
    create_meeting_bot: false,
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(EventTypeSchema) as any, // Type assertion to bypass complex type issues
    defaultValues: eventType
      ? {
        name: eventType.name,
        description: eventType.description || '',
        duration_minutes: eventType.duration_minutes,
        buffer_minutes: eventType.buffer_minutes || 0,
        slot_increment_minutes: eventType.slot_increment_minutes,
        location: eventType.location || '',
        is_active: eventType.status === EventTypeStatus.ACTIVE,
        autocreate_meeting: eventType.autocreate_meeting,
        create_meeting_bot: eventType.create_meeting_bot,

      }
      : defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  const autocreateMeeting = watch('autocreate_meeting');
  const createMeetingBot = watch('create_meeting_bot');

  useEffect(() => {
    if (eventType) {
      if (canAccess(FeatureType.MEETINGS_ENABLED) === false && eventType.create_meeting_bot === true) {
        methods.setValue('create_meeting_bot', false);
      }
    }

  }, [canAccess, eventType, methods]);

  useEffect(() => {
    if (eventType) {
      if (autocreateMeeting === true) {
        methods.setValue('location', "");
      }
    }

  }, [autocreateMeeting, eventType, methods]);

  const onSubmit = handleSubmit(async (data: FormValues) => {
    setSubmitting(true);
    try {
      if (eventType) {
        // Update existing event type
        const updateData: UpdateBookableEventTypeDto = {
          name: data.name,
          description: data.description || undefined,
          duration_minutes: data.duration_minutes,
          buffer_minutes: data.buffer_minutes ?? 0,
          slot_increment_minutes: data.slot_increment_minutes,
          location: data.location || undefined,
          autocreate_meeting: data.autocreate_meeting || false,
          create_meeting_bot: data.create_meeting_bot || false,
          status: data.is_active ? EventTypeStatus.ACTIVE : EventTypeStatus.INACTIVE,
        };

        await updateEventType({
          eventTypeId: eventType.id,
          data: updateData,
        }).unwrap();

        enqueueSnackbar(t('account.scheduling_settings.api.update.success'), { variant: 'success' });
      } else {
        // Create new event type
        const createData: CreateBookableEventTypeDto = {
          id: `event-type-${Date.now()}`, // Generate a unique ID
          name: data.name,
          description: data.description || undefined,
          duration_minutes: data.duration_minutes,
          slot_increment_minutes: data.slot_increment_minutes,
          buffer_minutes: data.buffer_minutes ?? 0,
          autocreate_meeting: data.autocreate_meeting || false,
          create_meeting_bot: data.create_meeting_bot || false,
          location: data.location || undefined
        };

        await createEventType({
          data: createData,
        }).unwrap();

        enqueueSnackbar(t('account.scheduling_settings.api.create.success'), { variant: 'success' });
      }

      onClose();
      reset(defaultValues);
      setSubmitting(false);
    } catch (error) {
      enqueueSnackbar(
        eventType
          ? t('account.scheduling_settings.api.update.default_error')
          : t('account.scheduling_settings.api.create.default_error'),
        { variant: 'error' }
      );
      setSubmitting(false);
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        {eventType ? t('account.scheduling_settings.edit_event_type') : t('account.scheduling_settings.add_event_type')}
      </DialogTitle>

      {
        canAccess(FeatureType.EVENT_SCHEDULING_ENABLED) ? (
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <DialogContent sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <RHFTextField
                    required
                    name="name"
                    label={t('account.scheduling_settings.event_name')}
                    placeholder={t('account.scheduling_settings.event_name_placeholder')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RHFTextField
                    name="description"
                    label={t('account.scheduling_settings.event_description')}
                    placeholder={t('account.scheduling_settings.event_description_placeholder')}
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField
                    required
                    name="duration_minutes"
                    label={t('account.scheduling_settings.duration_minutes')}
                    type="number"
                    InputProps={{ inputProps: { min: 5, max: 480 } }}
                    helperText={t('account.scheduling_settings.duration_helper')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField
                    required
                    name="slot_increment_minutes"
                    label={t('account.scheduling_settings.slot_increment_minutes')}
                    type="number"
                    InputProps={{ inputProps: { min: 10, max: 60 } }}
                    helperText={t('account.scheduling_settings.slot_increment_helper')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RHFTextField
                    name="buffer_minutes"
                    label={t('account.scheduling_settings.buffer_minutes')}
                    type="number"
                    InputProps={{ inputProps: { min: 0, max: 60 } }}
                    helperText={t('account.scheduling_settings.buffer_helper')}
                  />
                </Grid>

                {
                  autocreateMeeting === false && (
                    <Grid item xs={12}>
                      <RHFTextField
                        name="location"
                        label={t('account.scheduling_settings.location')}
                        placeholder={t('account.scheduling_settings.location_placeholder')}
                      />
                    </Grid>
                  )
                }

                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
                    <Stack direction="column" alignItems="start">
                      <Typography variant="subtitle2">
                        {t('account.scheduling_settings.autocreate_meeting')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {t('account.scheduling_settings.autocreate_meeting_helper')}
                      </Typography>
                    </Stack>
                    <RHFSwitch name="autocreate_meeting" />
                  </Stack>
                </Grid>

                {
                  autocreateMeeting === true && (
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
                        <Stack direction="column" alignItems="start">

                          <Stack direction="row" alignItems="center" justifyContent="start" spacing={1}>
                            <Typography variant="subtitle2">
                              {t('account.scheduling_settings.create_meeting_bot')}
                            </Typography>
                            {
                              !canAccess(FeatureType.MEETINGS_ENABLED) && (
                                <FeatureLockedChip text={t('common.upgrade_required')} variant='outlined' />
                              )
                            }

                          </Stack>

                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {t('account.scheduling_settings.create_meeting_bot_helper')}
                          </Typography>
                        </Stack>
                        <RHFSwitch name="create_meeting_bot" disabled={!canAccess(FeatureType.MEETINGS_ENABLED)} />
                      </Stack>
                    </Grid>
                  )
                }

                {
                  eventType && (
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
                        <Stack direction="column" alignItems="start">
                          <Typography variant="subtitle2">
                            {t('account.scheduling_settings.is_active')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {t('account.scheduling_settings.is_active_helper')}
                          </Typography>
                        </Stack>
                        <RHFSwitch name="is_active" />
                      </Stack>
                    </Grid>
                  )
                }

                {eventType && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      startIcon={<Iconify icon="mdi:calendar-clock" />}
                      onClick={availabilityModalControl.onTrue}
                      sx={{ mt: 1 }}
                    >
                      {t('account.event_type_availability.manage_button')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button variant="outlined" color="inherit" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {eventType ? t('common.save') : t('common.create')}
              </Button>
            </DialogActions>
          </FormProvider>
        ) : (
          <Box sx={{ p: 2, pb: 8 }}>
            <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
          </Box>
        )
      }

      {eventType && availabilityModalControl.value && (
        <EventTypeAvailabilityModal
          open={availabilityModalControl.value}
          onClose={availabilityModalControl.onFalse}
          eventType={eventType}
        />
      )}
    </Dialog>
  );
}